import { Menu, X, Lock } from "lucide-react";
import { C } from "../../theme/tokens";
import { getNavItems } from "./navItems";

/* ---------------------------------------------------------------
   NAVIGATION — burger button + slide-out drawer, replacing the old
   split (persistent left rail on wide, bottom tab bar on narrow)
   with a single pattern at every breakpoint: a slim topbar (burger,
   brand, "online" status) plus an overlay drawer that lists every
   destination. Two pieces are exported separately because they sit
   in different places in the layout — TopBar is part of the normal
   flow at the top of the screen, NavDrawer is fixed/overlaid and
   can be mounted anywhere — but they share one open/close state
   that the parent (App.jsx) owns.
------------------------------------------------------------------ */

export function TopBar({ onOpenMenu, t }) {
  return (
    <div
      style={{
        position: "relative",
        zIndex: 5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "14px 20px",
        flexShrink: 0,
      }}
    >
      <button
        onClick={onOpenMenu}
        aria-label={t.nav_menu_open}
        aria-expanded="false"
        style={{
          width: 38,
          height: 38,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: C.panel1,
          border: `1px solid ${C.hairline}`,
          borderRadius: 11,
          cursor: "pointer",
        }}
      >
        <Menu size={17} color={C.secondary} strokeWidth={2} />
      </button>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 14.5,
          letterSpacing: "0.02em",
          fontWeight: 600,
          color: C.primary,
          minWidth: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        <BrandMark />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>Market Maker Pro</span>
        <ProBadge />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          color: C.secondary,
          background: C.panel1,
          border: `1px solid ${C.hairline}`,
          padding: "6px 10px 6px 8px",
          borderRadius: 20,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: C.greenBright,
            animation: "livePulse 2.2s ease-out infinite",
          }}
        />
        {t.nav_status_online}
      </div>
    </div>
  );
}

export function NavDrawer({ open, onClose, tab, setTab, t, accessibleSections }) {
  const items = getNavItems(t, accessibleSections);

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden={!open}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(4,6,5,.6)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .25s ease",
          zIndex: 1200,
        }}
      />
      <nav
        aria-label={t.nav_menu_open}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: "78%",
          maxWidth: 300,
          boxSizing: "border-box",
          background: "#08090d",
          borderRight: `1px solid ${C.hairline}`,
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform .3s cubic-bezier(.4,0,.2,1)",
          zIndex: 1201,
          display: "flex",
          flexDirection: "column",
          padding: "22px 0 18px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px 18px",
            borderBottom: `1px solid ${C.hairline}`,
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14.5, fontWeight: 600, color: C.primary }}>
            <BrandMark />
            Market Maker Pro
            <ProBadge />
          </div>
          <button
            onClick={onClose}
            aria-label={t.nav_menu_close}
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: "transparent",
              border: `1px solid ${C.hairline}`,
              color: C.secondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <ul style={{ listStyle: "none", padding: "6px 10px", margin: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            const locked = accessibleSections ? !accessibleSections.has(key) : false;
            return (
              <li key={key}>
                <button
                  onClick={() => setTab(key)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "11px 12px",
                    borderRadius: 10,
                    background: active ? `${C.greenBright}21` : "transparent",
                    border: active ? `1px solid ${C.greenBright}40` : "1px solid transparent",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: active ? 600 : 500,
                    fontSize: 14,
                    letterSpacing: "-0.01em",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon size={18} strokeWidth={active ? 2.2 : 1.8} color={locked ? C.mutedA : active ? C.greenBright : C.mutedB} />
                  <span style={{ flex: 1, color: locked ? C.mutedA : active ? C.greenBright : C.secondary }}>{label}</span>
                  {locked && <Lock size={12.5} color={C.mutedA} />}
                </button>
              </li>
            );
          })}
        </ul>

        <div
          style={{
            marginTop: "auto",
            padding: "16px 20px 0",
            borderTop: `1px solid ${C.hairline}`,
            fontSize: 11,
            color: C.mutedB,
            lineHeight: 1.6,
          }}
        >
          Market Maker Pro
        </div>
      </nav>
    </>
  );
}

function BrandMark() {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: 6,
        flexShrink: 0,
        background: `linear-gradient(135deg, ${C.greenBright} 0%, #2f4bcf 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
        fontWeight: 700,
        color: "#f5f7ff",
      }}
    >
      MM
    </div>
  );
}

function ProBadge() {
  return (
    <span
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 9.5,
        fontWeight: 600,
        color: C.greenLight,
        background: `${C.greenBright}29`,
        border: `1px solid ${C.greenBright}4D`,
        padding: "2px 6px",
        borderRadius: 5,
        letterSpacing: "0.03em",
        flexShrink: 0,
      }}
    >
      PRO
    </span>
  );
}
